import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, HelpCircle, User, Settings, LogOut, Calendar, BarChart3, FileText, Users, Wrench, MessageCircle, BookOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import RunAsSelector from './RunAsSelector';
import NotificationBell from './NotificationBell';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const Header = ({ autoOpenChatWith = null, onAutoOpenChatHandled = null }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSupportMenuOpen, setIsSupportMenuOpen] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [adminCalendars, setAdminCalendars] = useState([]);
  const userMenuRef = useRef(null);
  const supportMenuRef = useRef(null);
  const { 
    isAuthenticated, 
    user, 
    loginUser,
    runningUser,
    isSuperAdmin,
    isCalendarAdmin,
    isCustomer,
    isRunAsMode, 
    getRunAsLevel,
    stopRunAs, 
    getRunAsReturnUrl,
    logout,
    getAuthHeaders
  } = useAuth();

  // Check if customer has access to reports feature
  const hasReportsFeature = runningUser?.plan_features?.reports === true;

  // Fetch calendars for calendar admin to determine menu link
  const fetchAdminCalendars = useCallback(async () => {
    if (!isAuthenticated || !runningUser?.id || runningUser?.role !== 'calendar_admin') return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/calendars`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.data) {
        setAdminCalendars(response.data.data);
      }
    } catch (err) {
      // Silent error - will default to manage calendars link
    }
  }, [isAuthenticated, runningUser?.id, runningUser?.role, getAuthHeaders]);

  // Fetch calendars on mount for calendar admins
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchAdminCalendars();
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchAdminCalendars]);

  // Determine calendar menu link based on number of calendars
  const getCalendarMenuLink = () => {
    if (adminCalendars.length === 1) {
      return `/calendar/${adminCalendars[0].id}`;
    }
    return '/admin/calendars';
  };

  const getCalendarMenuLabel = () => {
    if (adminCalendars.length === 1) {
      return 'Calendar';
    }
    return 'Calendars';
  };

  // Fetch support unread count
  const fetchSupportUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !runningUser?.id) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/unread/count`, {
        params: { message_type: 'support' },
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        setSupportUnreadCount(response.data.unread_count || 0);
      }
    } catch (err) {
      // Silent error
    }
  }, [isAuthenticated, runningUser?.id, getAuthHeaders]);

  // Fetch support unread count on mount and periodically
  useEffect(() => {
    // Use a timeout to avoid synchronous setState in effect
    const timeoutId = setTimeout(() => {
      fetchSupportUnreadCount();
    }, 0);
    
    const interval = setInterval(fetchSupportUnreadCount, 30000); // Every 30 seconds
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(interval);
    };
  }, [fetchSupportUnreadCount]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (supportMenuRef.current && !supportMenuRef.current.contains(event.target)) {
        setIsSupportMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStopRunAs = () => {
    const returnUrl = stopRunAs();
    window.location.href = returnUrl;
  };

  const handleLogout = () => {
    // If in Run As mode, just stop impersonation
    if (isRunAsMode()) {
      const returnUrl = stopRunAs();
      window.location.href = returnUrl;
      return;
    }
    
    // Store calendar symbolic name for redirect before logout
    const symbolicName = user?.calendar?.symbolic_name || runningUser?.calendar?.symbolic_name;
    let redirectPath = '/';
    if (symbolicName) {
      redirectPath = `/book/${symbolicName}`;
    }
    
    logout();
    navigate(redirectPath);
  };
  
  // Get the role label for Run As banner
  const getRunAsRoleLabel = () => {
    if (runningUser?.role === 'customer') {
      return 'Customer';
    }
    return 'Calendar Admin';
  };
  
  // Get banner color based on runningUser role
  const getBannerColor = () => {
    if (runningUser?.role === 'customer') {
      return {
        bg: 'bg-green-600',
        button: 'bg-green-700 hover:bg-green-800',
        text: 'text-green-200'
      };
    }
    return {
      bg: 'bg-orange-500',
      button: 'bg-orange-600 hover:bg-orange-700',
      text: 'text-orange-200'
    };
  };

  const bannerColors = getBannerColor();
  const runAsLevel = getRunAsLevel();

  return (
    <>
      {/* Run As Banner - shown when in Run As mode */}
      {isRunAsMode() && runningUser && (
        <div className={`${bannerColors.bg} text-white px-4 py-2 flex items-center justify-center gap-4`}>
          <div className="flex items-center gap-2">
            <span className="font-medium">
              🔄 Running as {getRunAsRoleLabel()}: <span className="font-bold">{runningUser.full_name}</span>
              <span className={`${bannerColors.text} ml-2`}>({runningUser.email})</span>
              {runAsLevel > 1 && (
                <span className={`${bannerColors.text} ml-2 text-sm`}>(Level {runAsLevel})</span>
              )}
            </span>
          </div>
          <button
            onClick={handleStopRunAs}
            className={`flex items-center gap-1 ${bannerColors.button} px-3 py-1 rounded-md text-sm font-medium transition-colors`}
            data-testid="stop-run-as-button"
          >
            <X className="h-4 w-4" />
            {runAsLevel > 1 ? 'Back to Previous' : 'Stop Run As'}
          </button>
        </div>
      )}
      
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-3xl font-bold">
                <span className="text-yellow-500">yi</span>
                <span className="text-gray-800">Booking</span>
              </span>
            </Link>
            
            <nav className="hidden md:flex items-center space-x-8">
              {/* Home - only show for non-logged in users */}
              {!isAuthenticated && (
                <Link 
                  to="/" 
                  className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  Home
                </Link>
              )}
              {/* Pricing - only show for non-logged in users */}
              {!isAuthenticated && (
                <Link 
                  to="/pricing" 
                  className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  Pricing
                </Link>
              )}
              {/* FAQ - only show for non-logged in users */}
              {!isAuthenticated && (
                <Link 
                  to="/faq" 
                  className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  FAQ
                </Link>
              )}
              {/* Find a Calendar - only show for non-logged in users */}
              {!isAuthenticated && (
                <Link 
                  to="/find-calendar" 
                  className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  Find a Calendar
                </Link>
              )}
              {/* Support - different behavior based on user role */}
              {/* Guest: shows text, Logged in: shows icon with tooltip */}
              {!isAuthenticated && (
                <Link 
                  to="/support" 
                  className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  Support
                </Link>
              )}
              
              {/* Calendar Admin Quick Access Icons */}
              {isAuthenticated && isCalendarAdmin && isCalendarAdmin() && (
                <div className="flex items-center space-x-2">
                  <Link 
                    to="/admin/customers" 
                    className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors group"
                    title="Manage Customers"
                    data-testid="menu-customers-icon"
                  >
                    <Users className="w-6 h-6" />
                    <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      Customers
                    </span>
                  </Link>
                  <Link 
                    to={getCalendarMenuLink()} 
                    className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors group"
                    title={adminCalendars.length === 1 ? "Open Calendar" : "My Calendars"}
                    data-testid="menu-calendars-icon"
                  >
                    <Calendar className="w-6 h-6" />
                    <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      {getCalendarMenuLabel()}
                    </span>
                  </Link>
                  <Link 
                    to="/admin/services" 
                    className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors group"
                    title="My Services"
                    data-testid="menu-services-icon"
                  >
                    <Wrench className="w-6 h-6" />
                    <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      Services
                    </span>
                  </Link>
                  <Link 
                    to="/manage-account" 
                    className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors group"
                    title="Manage Account"
                    data-testid="menu-account-icon"
                  >
                    <Settings className="w-6 h-6" />
                    <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      Account
                    </span>
                  </Link>
                </div>
              )}
              
              {/* Run As Selector for Super Admin */}
              {isAuthenticated && isSuperAdmin() && !isRunAsMode() && (
                <RunAsSelector />
              )}
              
              {/* Logging Management - Super Admin only */}
              {isAuthenticated && isSuperAdmin() && !isRunAsMode() && (
                <Link 
                  to="/admin/logging" 
                  className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors group"
                  title="Logging Management"
                  data-testid="logging-icon"
                >
                  <FileText className="w-6 h-6" />
                  <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    Logging
                  </span>
                </Link>
              )}
              
              {isAuthenticated ? (
                <div className="flex items-center space-x-2">
                  {/* Statistics Icon - for Customers with reports feature */}
                  {isCustomer() && hasReportsFeature && (
                    <Link 
                      to="/customer/statistics" 
                      className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors group"
                      title="Statistics"
                      data-testid="statistics-icon"
                    >
                      <BarChart3 className="w-6 h-6" />
                      {/* Tooltip */}
                      <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        Statistics
                      </span>
                    </Link>
                  )}
                  
                  {/* Support Menu - for logged in users (Super Admin, Calendar Admin & Customer) */}
                  {(runningUser?.role === 'super_admin' || runningUser?.role === 'calendar_admin' || runningUser?.role === 'customer') && (
                    <div className="relative" ref={supportMenuRef}>
                      <button 
                        onClick={() => setIsSupportMenuOpen(!isSupportMenuOpen)}
                        className={`relative p-2 text-gray-600 hover:text-gray-900 rounded-full transition-colors group ${
                          supportUnreadCount > 0 ? 'bg-pink-100 hover:bg-pink-200' : 'hover:bg-gray-100'
                        }`}
                        title={supportUnreadCount > 0 ? `Support (${supportUnreadCount} unread)` : 'Support'}
                        data-testid="support-icon"
                      >
                        <HelpCircle className="w-6 h-6" />
                        {/* Unread badge */}
                        {supportUnreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                            {supportUnreadCount > 9 ? '9+' : supportUnreadCount}
                          </span>
                        )}
                        {/* Tooltip - only show when menu is closed */}
                        {!isSupportMenuOpen && (
                          <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Support
                          </span>
                        )}
                      </button>
                      
                      {/* Support Dropdown Menu */}
                      {isSupportMenuOpen && (
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                          <div className="py-1">
                            {/* Support Chat Option */}
                            <button
                              onClick={() => {
                                setIsSupportMenuOpen(false);
                                if (runningUser?.role === 'customer') {
                                  window.dispatchEvent(new CustomEvent('openCustomerSupportChat'));
                                } else if (runningUser?.role === 'calendar_admin') {
                                  window.dispatchEvent(new CustomEvent('openCalendarAdminSupportChat'));
                                } else if (runningUser?.role === 'super_admin') {
                                  window.dispatchEvent(new CustomEvent('openSuperAdminSupportChat'));
                                }
                              }}
                              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              data-testid="support-chat-option"
                            >
                              <MessageCircle className="w-5 h-5 mr-3 text-yellow-500" />
                              <div className="text-left">
                                <div className="font-medium">Support Chat</div>
                                <div className="text-xs text-gray-500">Get help from our team</div>
                              </div>
                              {supportUnreadCount > 0 && (
                                <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  {supportUnreadCount}
                                </span>
                              )}
                            </button>
                            
                            {/* User Guide Option - Only for Calendar Admin */}
                            {runningUser?.role === 'calendar_admin' && (
                              <a
                                href="/user-guide"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setIsSupportMenuOpen(false)}
                                className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                data-testid="user-guide-option"
                              >
                                <BookOpen className="w-5 h-5 mr-3 text-blue-500" />
                                <div className="text-left">
                                  <div className="font-medium">User Guide</div>
                                  <div className="text-xs text-gray-500">Opens in new tab</div>
                                </div>
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Notification Bell - for all authenticated users */}
                  <NotificationBell 
                    autoOpenWith={autoOpenChatWith}
                    onAutoOpenHandled={onAutoOpenChatHandled}
                  />
                  
                  {/* Dashboard Icon - for Super Admin and Calendar Admin */}
                  {!isCustomer() && (
                    <Link 
                      to="/dashboard" 
                      className="relative p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full transition-colors group"
                      title="Dashboard"
                      data-testid="dashboard-icon"
                    >
                      <LayoutDashboard className="w-6 h-6" />
                      {/* Tooltip */}
                      <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        Dashboard
                      </span>
                    </Link>
                  )}
                  
                  {/* Calendar Icon - for Customers (Book Appointment) */}
                  {isCustomer() && (
                    <Link 
                      to="/customer/book-appointment" 
                      className="relative p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full transition-colors group"
                      title="Book Appointment"
                      data-testid="calendar-icon"
                    >
                      <Calendar className="w-6 h-6" />
                      {/* Tooltip */}
                      <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        Book Appointment
                      </span>
                    </Link>
                  )}
                  
                  {/* User Menu Icon with Dropdown */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors group"
                      data-testid="user-menu-icon"
                    >
                      <User className="w-6 h-6" />
                      {/* Tooltip - only show when menu is closed */}
                      {!isUserMenuOpen && (
                        <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          Account
                        </span>
                      )}
                    </button>
                    
                    {/* User Dropdown Menu */}
                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                        {/* User Info */}
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {runningUser?.full_name || 'User'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {runningUser?.email}
                          </p>
                        </div>
                        
                        {/* Menu Items */}
                        <div className="py-1">
                          <Link
                            to="/profile-settings"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            data-testid="profile-settings-link"
                          >
                            <Settings className="w-4 h-4 mr-3 text-gray-500" />
                            Profile Settings
                          </Link>
                          <button
                            onClick={() => {
                              setIsUserMenuOpen(false);
                              handleLogout();
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            data-testid="logout-button"
                          >
                            <LogOut className="w-4 h-4 mr-3" />
                            Logout
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Link 
                  to="/login" 
                  className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  Login
                </Link>
              )}
          </nav>
          
          <button 
            className="md:hidden text-gray-600 hover:text-gray-900"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 space-y-3">
            {/* Home - only show for non-logged in users */}
            {!isAuthenticated && (
              <Link 
                to="/" 
                className="block text-gray-600 hover:text-gray-900 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
            )}
            {/* Pricing - only show for non-logged in users */}
            {!isAuthenticated && (
              <Link 
                to="/pricing" 
                className="block text-gray-600 hover:text-gray-900 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Pricing
              </Link>
            )}
            {/* FAQ - only show for non-logged in users */}
            {!isAuthenticated && (
              <Link 
                to="/faq" 
                className="block text-gray-600 hover:text-gray-900 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                FAQ
              </Link>
            )}
            {/* Find a Calendar - only show for non-logged in users */}
            {!isAuthenticated && (
              <Link 
                to="/find-calendar" 
                className="block text-gray-600 hover:text-gray-900 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Find a Calendar
              </Link>
            )}
            {/* Support - show text for guests, will show as icon row for logged in */}
            {!isAuthenticated && (
              <Link 
                to="/support" 
                className="block text-gray-600 hover:text-gray-900 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Support
              </Link>
            )}
            
            {/* Calendar Admin Quick Access - Mobile Menu */}
            {isAuthenticated && isCalendarAdmin && isCalendarAdmin() && (
              <div className="pt-2 border-t border-gray-200 space-y-2">
                <Link
                  to="/admin/customers"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Users className="w-5 h-5" />
                  <span>Manage Customers</span>
                </Link>
                <Link
                  to={getCalendarMenuLink()}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Calendar className="w-5 h-5" />
                  <span>{adminCalendars.length === 1 ? 'My Calendar' : 'My Calendars'}</span>
                </Link>
                <Link
                  to="/admin/services"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Wrench className="w-5 h-5" />
                  <span>My Services</span>
                </Link>
                <Link
                  to="/manage-account"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Settings className="w-5 h-5" />
                  <span>Manage Account</span>
                </Link>
              </div>
            )}
            
            {isAuthenticated ? (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                {/* Icons row for mobile */}
                <div className="flex items-center space-x-3">
                  {/* Statistics Icon for Customers */}
                  {isCustomer() && hasReportsFeature && (
                    <Link 
                      to="/customer/statistics" 
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                      title="Statistics"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <BarChart3 className="w-6 h-6" />
                    </Link>
                  )}
                  {/* Support Icon - for all logged in users */}
                  {(runningUser?.role === 'super_admin' || runningUser?.role === 'calendar_admin' || runningUser?.role === 'customer') && (
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        if (runningUser?.role === 'customer') {
                          window.dispatchEvent(new CustomEvent('openCustomerSupportChat'));
                        } else if (runningUser?.role === 'calendar_admin') {
                          window.dispatchEvent(new CustomEvent('openCalendarAdminSupportChat'));
                        } else if (runningUser?.role === 'super_admin') {
                          window.dispatchEvent(new CustomEvent('openSuperAdminSupportChat'));
                        }
                      }}
                      className={`p-2 text-gray-600 hover:text-gray-900 rounded-full transition-colors ${
                        supportUnreadCount > 0 ? 'bg-pink-100 hover:bg-pink-200' : 'hover:bg-gray-100'
                      }`}
                      title={supportUnreadCount > 0 ? `Support (${supportUnreadCount} unread)` : 'Support'}
                    >
                      <HelpCircle className="w-6 h-6" />
                    </button>
                  )}
                  {/* Notification Bell for mobile */}
                  <NotificationBell 
                    autoOpenWith={autoOpenChatWith}
                    onAutoOpenHandled={onAutoOpenChatHandled}
                  />
                  {/* Dashboard Icon - for Super Admin and Calendar Admin */}
                  {!isCustomer() && (
                    <Link 
                      to="/dashboard" 
                      className="p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full transition-colors"
                      title="Dashboard"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <LayoutDashboard className="w-6 h-6" />
                    </Link>
                  )}
                  {/* Calendar Icon - for Customers */}
                  {isCustomer() && (
                    <Link 
                      to="/customer/book-appointment" 
                      className="p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full transition-colors"
                      title="Book Appointment"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Calendar className="w-6 h-6" />
                    </Link>
                  )}
                  {/* User Menu for mobile */}
                  <Link 
                    to="/profile-settings" 
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                    title="Profile Settings"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="w-6 h-6" />
                  </Link>
                </div>
                {/* Logout button for mobile */}
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center text-red-600 hover:text-red-700 transition-colors"
                >
                  <LogOut className="w-5 h-5 mr-1" />
                  <span className="text-sm">Logout</span>
                </button>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="block text-gray-600 hover:text-gray-900 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;